import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import JsBarcode from 'jsbarcode';
import { T } from './theme';
import { usePatientPhoto } from './EMRPhotos';

const API = 'https://tcio-api.onrender.com';
const hdrs = () => ({ headers: { Authorization: 'Bearer ' + localStorage.getItem('tcio_token') } });

const card = { background: T.surface, border: `1px solid ${T.b200}`, borderRadius: 10,
  boxShadow: '0 1px 2px rgba(110,59,71,.06), 0 4px 14px rgba(110,59,71,.07)' };
const btn = (primary) => ({ borderRadius: 8, padding: '9px 16px', font: 'inherit', fontWeight: 600,
  fontSize: 13, cursor: 'pointer', background: primary ? T.rose : '#fff',
  color: primary ? '#fff' : T.rose, border: primary ? 'none' : `1px solid ${T.b300}` });
const pill = (bg, fg, text) => <span style={{ background: bg, color: fg, borderRadius: 99,
  padding: '2px 10px', fontSize: 11.5, fontWeight: 600, whiteSpace: 'nowrap' }}>{text}</span>;
const REQ_PILL = {
  collected: () => pill(T.infoBg, T.info, 'Collected'),
  sent: () => pill(T.warningBg, T.warning, 'Sent to Lab'),
  resulted: () => pill(T.passBg, T.pass, 'Resulted'),
  voided: () => pill(T.b100, T.inkSoft, 'Voided'),
};
const TEST_CLASS = [
  [/benzodiazepine|alprazolam|clonazepam|diazepam|lorazepam|oxazepam|temazepam/i, 'Benzodiazepines'],
  [/buprenorphine|codeine|fentanyl|heroin|hydrocodone|hydromorphone|meperidine|methadone|morphine|oxycodone|oxymorphone|tapentadol|tramadol/i, 'Opioids'],
  [/amphetamine|cocaine|methylphenidate|phentermine|mdma/i, 'Stimulants'],
  [/butalbital|phenobarbital|secobarbital/i, 'Barbiturates'],
  [/alcohol|ethyl/i, 'Alcohol'],
  [/thc|spice/i, 'Cannabinoids'],
  [/bath salts|mdpv/i, 'Synthetic Cathinones'],
  [/kratom/i, 'Kratom'], [/ketamine|phencyclidine/i, 'Dissociatives'],
  [/gabapentin|pregabalin/i, 'Gabapentinoids'],
  [/trazodone|quetiapine/i, 'Psychotropics'],
  [/validity|creatinine|oxidant|- ph|specific/i, 'Validity'],
];
const testClass = (name) => { for (const [re, c] of TEST_CLASS) if (re.test(name || '')) return c; return 'Other'; };
const testKind = (name) => /validity|creatinine|oxidant|- ph|specific/i.test(name || '') ? 'Validity'
  : /screen/i.test(name || '') ? 'Screen' : 'Confirm';

const fmtDT = (v) => v ? new Date(v).toLocaleString('en-US',
  { timeZone: 'America/Los_Angeles', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';

function Avatar({ patientId, photoKey, first, last, size = 34 }) {
  const url = usePatientPhoto(patientId, !!photoKey);
  const initials = ((first || ' ')[0] + (last || ' ')[0]).toUpperCase();
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', overflow: 'hidden', flexShrink: 0,
      background: T.b100, border: `1px solid ${T.b200}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {url ? <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        : <span style={{ color: T.inkSoft, fontWeight: 700, fontSize: size * 0.36 }}>{initials}</span>}
    </div>
  );
}

function barcodeDataUrl(text) {
  const canvas = document.createElement('canvas');
  JsBarcode(canvas, text, { format: 'CODE128', width: 2, height: 56, displayValue: false, margin: 24 });
  return canvas.toDataURL('image/png');
}

// Save a file to the computer (2026-10-03). Fetches it and hands it to the browser as a download; if the browser refuses, opens it in a new tab.
async function saveUrlAsFile(url, filename) {
  try {
    const resp = await fetch(url); if (!resp.ok) throw new Error('fetch failed');
    const blob = await resp.blob(); const href = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = href; a.download = filename || 'lab-report.pdf';
    document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(href), 4000);
  } catch (e) { window.open(url, '_blank'); }
}

function labelHtml(p) {
  const r = p.requisition;
  const bc = barcodeDataUrl(r.specimen_id);
  const esc = (s) => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;');
  const cd = r.collected_at ? new Date(r.collected_at).toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles' }) : '';
  return `<div class="lbl">
    <div class="nm">${esc(r.last_name)}, ${esc(r.first_name)}</div>
    <img src="${bc}" alt="" />
    <div class="sid">${esc(r.specimen_id)} &middot; ${esc(r.specimen_source)}</div>
    <div class="sid">DOB ${r.dob ? new Date(r.dob).toLocaleDateString('en-US', { timeZone: 'UTC' }) : '________'} &middot; Collected ${cd || '________'}</div>
  </div>`;
}

// New / one-time order placed, collected, labeled and sent from the Labs board itself (2026-10-08). The board knows which
// facility it is on, so the order goes under the client's open stay at THIS facility and the screen says where it will be sent.
function BoardOrderFlow({ API, hdrs, T, btn, facilityId, flash, onChanged, onClose }) {
  const [info, setInfo] = useState({ patients: [], facility: null });
  const [panels, setPanels] = useState([]);
  const [prescribers, setPrescribers] = useState([]);
  const [pt, setPt] = useState(null);
  const [orders, setOrders] = useState([]);
  const [useOrder, setUseOrder] = useState('new');
  const [panelId, setPanelId] = useState('');
  const [providerId, setProviderId] = useState('');
  const [source, setSource] = useState('urine');
  const [tempOk, setTempOk] = useState('');
  const [tempF, setTempF] = useState('');
  const [tempSaved, setTempSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(null);
  const errText = (e) => (e.response && e.response.data && e.response.data.error) || e.message;
  useEffect(() => { if (!facilityId) return; (async () => {
    try {
      const [a, b, c] = await Promise.all([
        axios.get(`${API}/api/emr/labs/orderable-patients?facilityId=${facilityId}`, hdrs()),
        axios.get(`${API}/api/emr/labs`, hdrs()),
        axios.get(`${API}/api/emr/labs/prescribers?facility_id=${facilityId}`, hdrs()),
      ]);
      setInfo(a.data); setPanels(b.data.panels || []); setPrescribers(c.data.prescribers || []);
      if ((b.data.panels || []).length) setPanelId(b.data.panels[0].id);
    } catch (e) { setErr('Could not load: ' + errText(e)); }
  })(); }, [facilityId]); // eslint-disable-line react-hooks/exhaustive-deps
  const pick = async (p) => {
    setPt(p); setErr(''); setUseOrder('new'); setOrders([]);
    try {
      const r = await axios.get(`${API}/api/emr/labs/orders?patient_id=${p.patient_id}`, hdrs());
      const list = (r.data.orders || r.data.data || []).filter(o => o.facility_id === facilityId && (o.status === 'active' || o.status === 'pending_review'));
      setOrders(list); if (list.length) setUseOrder(list[0].id);
    } catch (e) { /* no existing orders shown; a new order can still be placed */ }
  };
  const go = async () => {
    setErr('');
    if (useOrder === 'new' && !providerId) { setErr('Choose the ordering provider.'); return; }
    setBusy(true);
    try {
      let orderId = useOrder, pending = false;
      if (useOrder === 'new') {
        const pr = prescribers.find(x => x.id === providerId);
        if (pr && !pr.has_npi) {
          const npi = window.prompt(pr.name + ' has no NPI on file, and the lab requires it. Enter the 10-digit NPI to save it and continue:', '');
          if (npi === null) { setBusy(false); return; }
          await axios.patch(`${API}/api/emr/labs/prescribers/${providerId}/npi`, { npi: npi.trim() }, hdrs());
        }
        const o = await axios.post(`${API}/api/emr/labs/orders`, { patient_id: pt.patient_id, episode_id: pt.episode_id, facility_id: facilityId, panel_id: panelId,
          lab_id: (panels.find(x => x.id === panelId) || {}).lab_id || null, ordered_by: providerId }, hdrs());
        orderId = o.data.order.id; pending = o.data.order.status === 'pending_review';
      } else { pending = (orders.find(o => o.id === useOrder) || {}).status === 'pending_review'; }
      const body = { order_id: orderId, specimen_source: source };
      const r = await axios.post(`${API}/api/emr/labs/requisitions`, body, hdrs());
      setDone({ id: r.data.requisition.id, specimen: r.data.requisition.specimen_id, pending, sent: false, urine: source === 'urine' });
      onChanged && onChanged();
    } catch (e) { setErr(errText(e)); }
    setBusy(false);
  };
  // The temperature is read AFTER the client gives the specimen, so it is recorded on the requisition, not when it is created.
  const saveTemp = async () => {
    if (tempOk === '') { setErr('Answer the temperature question.'); return; }
    if (tempOk === 'no' && !(Number(tempF) > 0)) { setErr('Enter the temperature that was read.'); return; }
    setBusy(true); setErr('');
    try { await axios.patch(`${API}/api/emr/labs/requisitions/${done.id}/clinical-info`, { temp_in_range: tempOk === 'yes', temp_c: tempOk === 'no' ? Math.round(((Number(tempF) - 32) * 5 / 9) * 10) / 10 : null }, hdrs()); setTempSaved(true); }
    catch (e) { setErr(errText(e)); }
    setBusy(false);
  };
  const label = async () => { try { const x = await axios.get(`${API}/api/emr/labs/requisitions/${done.id}/print`, hdrs()); printLabels([x.data]); } catch (e) { setErr(errText(e)); } };
  const sendIt = async () => {
    setBusy(true); setErr('');
    try { await axios.post(`${API}/api/emr/labs/requisitions/${done.id}/send`, {}, hdrs()); setDone(d => ({ ...d, sent: true })); flash('Sent to lab'); onChanged && onChanged(); }
    catch (e) { setErr(errText(e)); }
    setBusy(false);
  };
  const fac = info.facility || {};
  const where = fac.account_number ? 'Orders placed here go to the lab electronically under account ' + fac.account_number + '.' : 'This facility sends paper requisitions.';
  const cap = { fontSize: 11, fontWeight: 700, color: T.inkSoft, textTransform: 'uppercase', letterSpacing: 0.5, margin: '10px 0 4px' };
  const inp = { width: '100%', padding: '7px 8px', borderRadius: 8, border: `1px solid ${T.b100}`, fontSize: 13, boxSizing: 'border-box' };
  const panelName = (id) => (panels.find(x => x.id === id) || {}).name || 'Lab order';
  return (<>
    <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 2 }}>New / One-Time Order</div>
    <div style={{ fontSize: 12, color: T.inkSoft, marginBottom: 10 }}>{fac.org_name ? fac.org_name + '. ' : ''}{where}</div>
    {err && <div style={{ fontSize: 13, fontWeight: 700, color: '#B42318', background: '#FEF3F2', borderRadius: 8, padding: '8px 10px', marginBottom: 8 }}>{err}</div>}
    {!pt && <>
      {(info.patients || []).map(p => (
        <div key={p.patient_id} onClick={() => pick(p)}
          style={{ padding: '8px 10px', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13, borderBottom: `1px solid ${T.b100}` }}>
          {p.last_name}, {p.first_name} <span style={{ color: T.inkSoft, fontWeight: 400, fontSize: 11 }}>{[p.loc, p.mrn].filter(Boolean).join(' | ')}</span>
        </div>
      ))}
      {!(info.patients || []).length && <div style={{ color: T.inkSoft, fontSize: 13 }}>No current clients at this facility.</div>}
    </>}
    {pt && !done && <>
      <div style={{ fontWeight: 700, fontSize: 14 }}>{pt.last_name}, {pt.first_name} <span style={{ color: T.inkSoft, fontWeight: 400, fontSize: 12 }}>{pt.loc || ''}</span>
        <span onClick={() => { setPt(null); setErr(''); }} style={{ marginLeft: 10, fontSize: 12, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}>change</span></div>
      {orders.length > 0 && <>
        <div style={cap}>Order</div>
        <select style={inp} value={useOrder} onChange={e => setUseOrder(e.target.value)}>
          {orders.map(o => <option key={o.id} value={o.id}>{panelName(o.panel_id)}{o.created_at ? ' - ordered ' + new Date(o.created_at).toLocaleDateString('en-US') : ''}{o.ordered_by_name ? ' by ' + o.ordered_by_name : ''}{o.status === 'active' ? '' : ' (awaiting signature)'}</option>)}
          <option value="new">New one-time order</option>
        </select>
      </>}
      {useOrder === 'new' && <>
        <div style={cap}>Ordering provider</div>
        <select style={inp} value={providerId} onChange={e => setProviderId(e.target.value)}>
          <option value="">Choose the ordering provider</option>
          {prescribers.map(x => <option key={x.id} value={x.id}>{x.name}{x.credentials ? ', ' + x.credentials : ''}{x.has_npi ? '' : ' (no NPI on file)'}</option>)}
        </select>
        <div style={cap}>Panel</div>
        <select style={inp} value={panelId} onChange={e => setPanelId(e.target.value)}>
          {panels.map(x => <option key={x.id} value={x.id}>{x.name} ({x.test_count} tests)</option>)}
        </select>
      </>}
      <div style={cap}>Specimen</div>
      <select style={inp} value={source} onChange={e => setSource(e.target.value)}>
        <option value="urine">Urine</option>
        <option value="oral">Oral fluid</option>
      </select>
      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button style={btn(true)} disabled={busy} onClick={go}>{busy ? 'Working...' : (useOrder === 'new' ? 'Order & create requisition' : 'Create requisition')}</button>
        <button style={btn(false)} disabled={busy} onClick={onClose}>Cancel</button>
      </div>
    </>}
    {done && <>
      <div style={{ fontWeight: 800, fontSize: 14 }}>Requisition created: {done.specimen}</div>
      <div style={{ fontSize: 13, color: T.inkSoft, margin: '4px 0 6px' }}>{pt.last_name}, {pt.first_name}.{done.pending ? ' The order has gone to the provider to sign.' : ''}</div>
      <div style={cap}>1. Label the cup</div>
      <button style={btn(false)} onClick={label}>Print label</button>
      {done.urine && <>
        <div style={cap}>2. After the specimen is collected: temperature read within 4 minutes and in range (90.5 - 99.8 F)?</div>
        <select style={inp} value={tempOk} disabled={tempSaved} onChange={e => setTempOk(e.target.value)}>
          <option value="">Choose</option>
          <option value="yes">Yes, in range</option>
          <option value="no">No, out of range</option>
        </select>
        {tempOk === 'no' && <>
          <div style={cap}>Temperature read (F)</div>
          <input style={inp} type="number" step="0.1" value={tempF} disabled={tempSaved} onChange={e => setTempF(e.target.value)} placeholder="for example 88.5" />
        </>}
        <div style={{ marginTop: 8 }}><button style={btn(false)} disabled={busy || tempSaved} onClick={saveTemp}>{tempSaved ? 'Temperature recorded' : 'Save temperature'}</button></div>
      </>}
      <div style={cap}>{done.urine ? '3' : '2'}. Send</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button style={btn(true)} disabled={busy || done.sent || (done.urine && !tempSaved)} onClick={sendIt}>{done.sent ? 'Sent' : (busy ? 'Sending...' : 'Send to lab')}</button>
        <button style={btn(false)} onClick={onClose}>{done.sent ? 'Done' : 'Finish later'}</button>
      </div>
      {!done.sent && <div style={{ fontSize: 12, color: T.inkSoft, marginTop: 8 }}>If you finish later, the specimen stays on the board until it is sent.</div>}
    </>}
  </>);
}

// Record (or correct) a urine specimen's temperature check from its row on the board (2026-10-08). The reading is taken after
// the client gives the specimen, which can be long after the requisition was created, and it must be on file before sending.
function BoardTempModal({ API, hdrs, T, btn, card, req, flash, onClose, onSaved }) {
  const [ok, setOk] = useState(req.temp_in_range === true ? 'yes' : req.temp_in_range === false ? 'no' : '');
  const [f, setF] = useState(req.temp_c != null && req.temp_c !== '' ? String(Math.round((Number(req.temp_c) * 9 / 5 + 32) * 10) / 10) : '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const save = async () => {
    if (ok === '') { setErr('Answer the temperature question.'); return; }
    if (ok === 'no' && !(Number(f) > 0)) { setErr('Enter the temperature that was read.'); return; }
    setBusy(true); setErr('');
    try {
      await axios.patch(`${API}/api/emr/labs/requisitions/${req.id}/clinical-info`, { temp_in_range: ok === 'yes', temp_c: ok === 'no' ? Math.round(((Number(f) - 32) * 5 / 9) * 10) / 10 : null }, hdrs());
      flash('Temperature recorded'); onSaved && onSaved(); onClose();
    } catch (e) { setErr((e.response && e.response.data && e.response.data.error) || e.message); }
    setBusy(false);
  };
  const cap = { fontSize: 11, fontWeight: 700, color: T.inkSoft, textTransform: 'uppercase', letterSpacing: 0.5, margin: '10px 0 4px' };
  const inp = { width: '100%', padding: '7px 8px', borderRadius: 8, border: `1px solid ${T.b100}`, fontSize: 13, boxSizing: 'border-box' };
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ ...card, width: '100%', maxWidth: 420, padding: 18 }}>
        <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 2 }}>Specimen temperature</div>
        <div style={{ fontSize: 12, color: T.inkSoft, marginBottom: 6 }}>{req.last_name}, {req.first_name} | {req.specimen_id}</div>
        {err && <div style={{ fontSize: 13, fontWeight: 700, color: '#B42318', background: '#FEF3F2', borderRadius: 8, padding: '8px 10px', marginBottom: 8 }}>{err}</div>}
        <div style={cap}>Temperature read within 4 minutes and in range (90.5 - 99.8 F)?</div>
        <select style={inp} value={ok} onChange={e => setOk(e.target.value)}>
          <option value="">Choose</option>
          <option value="yes">Yes, in range</option>
          <option value="no">No, out of range</option>
        </select>
        {ok === 'no' && <>
          <div style={cap}>Temperature read (F)</div>
          <input style={inp} type="number" step="0.1" value={f} onChange={e => setF(e.target.value)} placeholder="for example 88.5" />
        </>}
        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <button style={btn(true)} disabled={busy} onClick={save}>{busy ? 'Saving...' : (req.__thenSend ? 'Save and send to lab' : 'Save temperature')}</button>
          <button style={btn(false)} disabled={busy} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function printLabels(payloads) {
  const w = window.open('', '_blank');
  if (!w) return alert('Pop-up blocked - allow pop-ups for this site to print.');
  w.document.write(`<!DOCTYPE html><html><head><title>Specimen Labels</title><style>
    @page { size: 2.25in 1.25in; margin: 0; }
    body { margin: 0; font-family: -apple-system, Helvetica, Arial, sans-serif; }
    .lbl { width: 2.25in; height: 1.25in; box-sizing: border-box; padding: 4px 6px;
      page-break-after: always; display: flex; flex-direction: column; justify-content: center; }
    .nm { font-size: 10px; font-weight: 700; white-space: nowrap; overflow: hidden; }
    .lbl img { width: 100%; height: 0.5in; object-fit: fill; image-rendering: pixelated; }
    .sid { font-size: 8px; letter-spacing: 0.3px; white-space: nowrap; line-height: 1.25; }
  </style></head><body>${payloads.map(labelHtml).join('')}</body></html>`);
  w.document.close();
  setTimeout(() => { w.focus(); w.print(); }, 400);
}

function requisitionHtml(p) {
  const r = p.requisition, tests = p.tests || [];
  const ins = r.insurance_snapshot || {};
  const meds = Array.isArray(r.meds_snapshot) ? r.meds_snapshot : [];
  const dx = Array.isArray(r.dx_snapshot) ? r.dx_snapshot : [];
  const primaryDx = (dx.find(d => d.is_primary) || dx[0] || {});
  const secondary = dx.filter(d => d.code !== primaryDx.code).map(d => d.code).join(', ');
  const bc = barcodeDataUrl(r.specimen_id);
  const dob = r.dob ? new Date(r.dob).toLocaleDateString('en-US', { timeZone: 'UTC' }) : 'N/A';
  const md = ((r.md_first || '') + ' ' + (r.md_last || '')).trim();
  const esc = (s) => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;');
  return `
  <div class="req-page">
    <div class="hdr">
      <h1>Lab Test Requisition</h1>
      <div class="bc"><img src="${bc}" alt="" /><div class="bcid">${esc(r.specimen_id)}</div></div>
    </div>
    <table class="grid">
      <tr><td><b>Specimen</b><br/>${esc(r.specimen_id)}</td>
          <td><b>Specimen Source</b><br/>${esc(r.specimen_source)}</td>
          <td><b>Status</b><br/>${esc(r.status)}</td></tr>
      <tr><td><b>Collected At</b><br/>${fmtDT(r.collected_at)}</td>
          <td><b>Collected By</b><br/>${esc(r.collected_by_name || '')}</td>
          <td><b>Sent At</b><br/>${fmtDT(r.sent_at)}</td></tr>
    </table>
    <h2>Patient</h2>
    <table class="grid">
      <tr><td><b>Name</b><br/>${esc(r.first_name)} ${esc(r.last_name)}</td>
          <td><b>MRN</b><br/>${esc(r.mrn || 'N/A')}</td>
          <td><b>DOB</b><br/>${dob}</td></tr>
    </table>
    <h2>Insurance</h2>
    <table class="grid">
      <tr><td><b>Payer</b><br/>${esc(ins.primary_payer || 'N/A')}</td>
          <td><b>Member ID</b><br/>${esc(ins.member_id || 'N/A')}</td>
          <td><b>Group</b><br/>${esc(ins.group_number || 'N/A')}</td></tr>
    </table>
    <h2>Lab</h2>
    <table class="grid">
      <tr><td><b>Name</b><br/>${esc(r.lab_name || 'MILLENNIUM HEALTH')}</td>
          <td><b>Account</b><br/>${esc(r.account_number || '')}</td>
          <td><b>CLIA</b><br/>${esc(r.clia_code || '')}</td></tr>
    </table>
    <h2>Ordering Provider</h2>
    <div class="line">${esc(md)}${r.md_credentials ? ', ' + esc(r.md_credentials) : ''}</div>
    <h2>Statement of Medical Necessity</h2>
    <div class="necessity">${esc(r.medical_necessity || '')}</div>
    <h2>Requested Tests (${tests.length})</h2>
    <table class="tests">
      <tr><th>Test Code</th><th>Description</th><th>Type</th><th>Primary Dx</th><th>Secondary Dx</th></tr>
      ${tests.map(t => `<tr><td>${esc(t.test_code || '')}</td><td>${esc(t.test_name)}</td><td>${esc(t.test_type)}</td><td>${esc(primaryDx.code || '')}</td><td>${esc(secondary)}</td></tr>`).join('')}
    </table>
    <h2>Clinical Information</h2>
    <div class="line">${esc(r.clinical_info || '')}</div>
    <h2>All Diagnosis Codes</h2>
    <div class="line">${esc(dx.map(d => d.code + ' ' + (d.description || '')).join(', ') || 'None on file')}</div>
    <h2>Medications</h2>
    <div class="line">${esc(meds.join(', ') || 'None active')}</div>
    <div class="foot">Powered by TCIO — Treatment Center Intelligence OS</div>
  </div>`;
}

function printRequisitions(payloads) {
  const w = window.open('', '_blank');
  if (!w) return alert('Pop-up blocked - allow pop-ups for this site to print.');
  w.document.write(`<!DOCTYPE html><html><head><title>Lab Requisitions</title><style>
    body { font-family: -apple-system, Helvetica, Arial, sans-serif; color: #111; margin: 0; }
    .req-page { padding: 32px 40px; page-break-after: always; }
    .hdr { display: flex; justify-content: space-between; align-items: flex-start; }
    .hdr h1 { font-size: 20px; margin: 0 0 12px; }
    .bc { text-align: right; } .bc img { height: 56px; } .bcid { font-size: 12px; letter-spacing: 1px; font-weight: 700; }
    h2 { font-size: 13px; margin: 16px 0 6px; border-bottom: 1px solid #ccc; padding-bottom: 3px; }
    .grid { width: 100%; border-collapse: collapse; font-size: 12px; }
    .grid td { padding: 4px 8px 4px 0; vertical-align: top; width: 33%; }
    .tests { width: 100%; border-collapse: collapse; font-size: 11px; }
    .tests th, .tests td { border-bottom: 1px solid #ddd; text-align: left; padding: 3px 6px; }
    .necessity { font-size: 12px; font-family: monospace; white-space: pre-wrap; }
    .line { font-size: 12px; }
    .foot { margin-top: 24px; font-size: 10px; color: #888; }
    @media print { .req-page { padding: 12px 8px; } }
  </style></head><body>${payloads.map(requisitionHtml).join('')}</body></html>`);
  w.document.close();
  setTimeout(() => { w.focus(); w.print(); }, 400);
}

export default function LabsBoard({ user, initialFacilityName, patients, onOpenChart }) {
  const [facilities, setFacilities] = useState([]);
  const [facilityId, setFacilityId] = useState('');
  const [orders, setOrders] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [checked, setChecked] = useState({});
  const [reqChecked, setReqChecked] = useState({});
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [lastBatch, setLastBatch] = useState([]);
  const [openTests, setOpenTests] = useState({});
  const [excluded, setExcluded] = useState({});
  const [viewReq, setViewReq] = useState(null);       // print payload for the open requisition
  const [viewList, setViewList] = useState([]);       // ids being paged through
  const [viewIdx, setViewIdx] = useState(0);
  const [viewTab, setViewTab] = useState('req');      // req | tests | history
  const [stagedEx, setStagedEx] = useState({});       // key -> true = excluded (staged, unsaved)
  const [testsDirty, setTestsDirty] = useState(false);
  const [orderTests, setOrderTests] = useState([]);   // full order test list for the open requisition
  const [showResulted, setShowResulted] = useState(false);
  const [labAcct, setLabAcct] = useState(null);       // this facility's lab account number; null = paper requisitions
  const [tempFor, setTempFor] = useState(null);       // requisition row whose temperature is being recorded
  const [picker, setPicker] = useState(null);         // 'order' | 'upload' | 'labs' | null
  const [labsDir, setLabsDir] = useState([]);
  const [pendingOrders, setPendingOrders] = useState([]);
  const fileRefs = useRef({});

  const flash = (msg, ok = true) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 4000); };

  useEffect(() => { (async () => {
    try {
      const r = await axios.get(`${API}/api/facilities?emr_live=1`, hdrs());
      const raw = r.data.data || [];
      const fac = [
        ...[...raw].sort((a, b) => a.org_name.localeCompare(b.org_name)),
      ];
      setFacilities(fac);
      const pre = initialFacilityName && fac.find(x => x.org_name === initialFacilityName);
      setFacilityId(((pre || fac[0]) || {}).id || '');
    } catch (e) { flash('Failed to load facilities', false); }
  })(); }, [initialFacilityName]);

  // Does this facility send to the lab electronically? Decides whether the send buttons say Send to Lab or Mark Sent.
  useEffect(() => { if (!facilityId) return; setLabAcct(null);
    axios.get(`${API}/api/emr/labs/orderable-patients?facilityId=${facilityId}`, hdrs()).then(r => setLabAcct((r.data.facility || {}).account_number || null)).catch(() => {});
  }, [facilityId]);

  const load = useCallback(async () => {
    if (!facilityId) return;
    try {
      const [o, r, pv] = await Promise.all([
        axios.get(`${API}/api/emr/labs/standing-orders?facilityId=${facilityId}`, hdrs()),
        axios.get(`${API}/api/emr/labs/requisitions?facilityId=${facilityId}`, hdrs()),
        axios.get(`${API}/api/emr/labs/orders/pending-review?facilityId=${facilityId}`, hdrs()).catch(() => null),
      ]);
      setOrders(o.data.data || []);
      setReqs(r.data.data || []);
      setPendingOrders((pv && pv.data.data) || []);
    } catch (e) { flash('Failed to load labs board', false); }
  }, [facilityId]);
  useEffect(() => { load(); }, [load]);

  // ---------- generation ----------
  const nChecked = Object.values(checked).filter(Boolean).length;
  const toggleAll = () => {
    if (nChecked === orders.length) setChecked({});
    else setChecked(Object.fromEntries(orders.map(o => [o.order_id, true])));
  };

  const generate = async () => {
    const ids = orders.filter(o => checked[o.order_id]).map(o => o.order_id);
    if (!ids.length) return;
    setBusy(true);
    try {
      const exclusions = {};
      for (const oid of ids) {
        const ex = Object.keys(excluded[oid] || {}).filter(k => excluded[oid][k]);
        if (ex.length) exclusions[oid] = ex;
      }
      const r = await axios.post(`${API}/api/emr/labs/requisitions/batch`, { order_ids: ids, exclusions }, hdrs());
      const created = r.data.created || [], errors = r.data.errors || [];
      flash(`${created.length} requisition${created.length === 1 ? '' : 's'} created` +
        (errors.length ? ` · ${errors.length} failed` : ''), !errors.length);
      setChecked({}); setLastBatch(created.map(c => c.id));
      await load();
    } catch (e) { flash((e.response && e.response.data && e.response.data.error) || 'Batch failed', false); }
    finally { setBusy(false); }
  };

  const fetchPayloads = (ids) => Promise.all(ids.map(id =>
    axios.get(`${API}/api/emr/labs/requisitions/${id}/print`, hdrs()).then(x => x.data)));

  const printBatch = async (ids, asLabels) => {
    if (!ids.length) return;
    try { (asLabels ? printLabels : printRequisitions)(await fetchPayloads(ids)); }
    catch (e) { flash('Print failed', false); }
  };

  const toggleTests = async (o) => {
    if (openTests[o.order_id]) { setOpenTests(t => ({ ...t, [o.order_id]: null })); return; }
    try {
      const r = await axios.get(`${API}/api/emr/labs/orders/${o.order_id}/tests`, hdrs());
      setOpenTests(t => ({ ...t, [o.order_id]: r.data.data || [] }));
    } catch (e) { flash('Failed to load tests', false); }
  };

  // ---------- requisition viewer with paging ----------
  const openReqAt = async (ids, idx, keepTab) => {
    try {
      const p = await axios.get(`${API}/api/emr/labs/requisitions/${ids[idx]}/print`, hdrs());
      setViewList(ids); setViewIdx(idx); setViewReq(p.data);
      if (!keepTab) setViewTab('req');
      // Full order test list -> staged inclusion state (snapshot tests = included)
      const ot = await axios.get(`${API}/api/emr/labs/orders/${p.data.requisition.order_id}/tests`, hdrs()).catch(() => null);
      const all = (ot && ot.data.data) || [];
      setOrderTests(all);
      const inSnap = new Set((p.data.tests || []).map(t => t.test_code || t.test_name));
      const ex = {};
      for (const t of all) { const k = t.test_code || t.test_name; if (!inSnap.has(k)) ex[k] = true; }
      setStagedEx(ex); setTestsDirty(false);
    } catch (e) { flash('Failed to load requisition', false); }
  };

  const saveTests = async (closeAfter) => {
    const r = viewReq.requisition;
    const excluded_keys = Object.keys(stagedEx).filter(k => stagedEx[k]);
    try {
      await axios.put(`${API}/api/emr/labs/requisitions/${r.id}/tests`, { excluded_keys }, hdrs());
      flash('Test selection saved');
      if (closeAfter) { setViewReq(null); load(); }
      else openReqAt(viewList, viewIdx, true);
    } catch (e) { flash((e.response && e.response.data && e.response.data.error) || 'Save failed', false); }
  };
  const pageView = (dir) => {
    const ni = viewIdx + dir;
    if (ni < 0 || ni >= viewList.length) return;
    openReqAt(viewList, ni);
  };
  useEffect(() => {
    if (!viewReq) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setViewReq(null);
      if (e.key === 'ArrowRight') pageView(1);
      if (e.key === 'ArrowLeft') pageView(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const openSingle = (id) => {
    const sel = reqs.filter(r => reqChecked[r.id]).map(r => r.id);
    if (sel.length > 1 && sel.includes(id)) openReqAt(sel, sel.indexOf(id));
    else openReqAt([id], 0);
  };

  const nReqChecked = Object.values(reqChecked).filter(Boolean).length;
  const openChecked = () => {
    const sel = reqs.filter(r => reqChecked[r.id]).map(r => r.id);
    if (sel.length) openReqAt(sel, 0);
  };

  const send = async (id, silent) => {
    try {
      await axios.post(`${API}/api/emr/labs/requisitions/${id}/send`, {}, hdrs());
      if (!silent) { flash(labAcct ? 'Sent to lab' : 'Marked sent'); load(); }
      return true;
    } catch (e) {
      if (e.response && e.response.data && e.response.data.needs_temp) {
        const row = reqs.find(x => x.id === id);
        if (row) { setTempFor({ ...row, __thenSend: true }); if (!silent) flash('Record the specimen temperature and it will send'); return false; }
      }
      if (!silent) flash((e.response && e.response.data && e.response.data.error) || 'Send failed', false); return false; }
  };

  // Temperature queue (11/1, Trina 5c256c43): rows that need a temperature are walked one at a time; each sends as its temperature is saved.
  const tempQueueRef = useRef([]);
  const openNextTemp = () => {
    const id = tempQueueRef.current.shift();
    if (!id) { setReqChecked({}); load(); return; }
    const row = reqs.find(x => x.id === id);
    if (row) setTempFor({ ...row, __thenSend: true }); else openNextTemp();
  };
  const tempSaved = async () => {
    const t = tempFor; setTempFor(null);
    if (t && t.__thenSend) { await send(t.id, true); flash(labAcct ? 'Sent to lab' : 'Marked sent'); openNextTemp(); } else load();
  };
  const needsTemp = (r) => String(r.specimen_source || '').toLowerCase() === 'urine' && r.temp_in_range !== true && r.temp_in_range !== false;
  const batchSend = async () => {
    const sel = reqs.filter(r => reqChecked[r.id] && r.status === 'collected');
    if (!sel.length) return flash('No collected requisitions selected', false);
    const ready = sel.filter(r => !needsTemp(r)), need = sel.filter(needsTemp);
    let ok = 0;
    for (const r of ready) { if (await send(r.id, true)) ok++; }
    if (need.length) {
      flash(`${ok} sent. ${need.length} still need a temperature: record each one and it will send.`);
      tempQueueRef.current = need.map(r => r.id); openNextTemp();
    } else { flash(`${ok} of ${sel.length} ${labAcct ? 'sent to lab' : 'marked sent'}`); setReqChecked({}); load(); }
  };

  const dropPdf = async (id, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API}/api/emr/labs/requisitions/${id}/result-pdf`, fd,
        { headers: { ...hdrs().headers, 'Content-Type': 'multipart/form-data' } });
      flash('Result attached — requisition resulted');
      setPicker(null); load();
    } catch (e) { flash((e.response && e.response.data && e.response.data.error) || 'Upload failed', false); }
  };

  const saveClinicalInfo = async (id, text) => {
    try {
      await axios.patch(`${API}/api/emr/labs/requisitions/${id}/clinical-info`, { clinical_info: text }, hdrs());
      flash('Clinical info saved'); openReqAt(viewList, viewIdx);
    } catch (e) { flash((e.response && e.response.data && e.response.data.error) || 'Save failed', false); }
  };

  const removeTest = async (reqId, testId, name) => {
    if (!window.confirm(`Remove ${name} from this requisition?`)) return;
    try {
      await axios.delete(`${API}/api/emr/labs/requisitions/${reqId}/tests/${testId}`, hdrs());
      flash(`${name} removed`); openReqAt(viewList, viewIdx);
    } catch (e) { flash((e.response && e.response.data && e.response.data.error) || 'Remove failed', false); }
  };

  const openLabsDir = async () => {
    try {
      const r = await axios.get(`${API}/api/emr/labs`, hdrs());
      setLabsDir(r.data.data || r.data.labs || []);
      setPicker('labs');
    } catch (e) { flash('Failed to load labs', false); }
  };

  // ---------- derived ----------
  const daysSince = (v) => v ? Math.floor((Date.now() - new Date(v).getTime()) / 86400000) : null;
  const todayPT = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
  const isTodayPT = (v) => v && new Date(v).toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' }) === todayPT;
  const summary = {
    collectedToday: reqs.filter(r => isTodayPT(r.collected_at)).length,
    pending: reqs.filter(r => r.status === 'sent').length,
    standing: orders.length,
    resulted: reqs.filter(r => r.status === 'resulted').length,
  };
  const activeReqs = reqs.filter(r => r.status !== 'resulted' || showResulted);
  const resultedReqs = reqs.filter(r => r.status === 'resulted');

  const statBox = (n, l, color) => (
    <div key={l} style={{ ...card, padding: '10px 16px', textAlign: 'center', minWidth: 110 }}>
      <div style={{ fontSize: 22, fontWeight: 900, color: color || T.ink }}>{n}</div>
      <div style={{ fontSize: 11, color: T.inkSoft, fontWeight: 700 }}>{l}</div>
    </div>
  );

  const ReqRow = (r) => (
    <tr key={r.id} style={{ borderTop: `1px solid ${T.b100}` }}>
      <td style={{ padding: 6 }}><input type="checkbox" checked={!!reqChecked[r.id]}
        onChange={e => setReqChecked(c => ({ ...c, [r.id]: e.target.checked }))} /></td>
      <td style={{ padding: 6, fontWeight: 700, cursor: 'pointer' }} onClick={() => openSingle(r.id)}>{r.last_name}, {r.first_name}
        <div style={{ fontSize: 11, color: T.inkSoft, fontWeight: 400 }}>{r.mrn}</div></td>
      <td style={{ fontFamily: 'monospace', fontSize: 12, cursor: 'pointer', color: T.rose, fontWeight: 700 }}
        onClick={() => openSingle(r.id)}>{r.specimen_id}</td>
      <td>{r.specimen_source}</td>
      <td>{fmtDT(r.collected_at)}</td>
      <td>{(REQ_PILL[r.status] || (() => r.status))()}</td>
      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        <button style={{ ...btn(false), padding: '5px 10px', marginRight: 6 }} onClick={() => printBatch([r.id], false)}>🖨 Req</button>
        {r.status === 'collected' && String(r.specimen_source || '').toLowerCase() === 'urine' && <button style={{ ...btn(false), padding: '5px 10px', marginRight: 6 }} onClick={() => setTempFor(r)}>{(r.temp_in_range === true || r.temp_in_range === false) ? 'Temp recorded' : 'Record temp'}</button>}
        <button style={{ ...btn(false), padding: '5px 10px', marginRight: 6 }} onClick={() => printBatch([r.id], true)}>🏷 Label</button>
        {r.status === 'collected' &&
          <button style={{ ...btn(true), padding: '5px 10px', marginRight: 6 }} onClick={() => send(r.id)}>{labAcct ? 'Send to Lab' : 'Mark Sent'}</button>}
        {(r.status === 'sent' || r.status === 'collected') && <>
          <input type="file" accept="application/pdf" style={{ display: 'none' }}
            ref={el => { fileRefs.current[r.id] = el; }}
            onChange={e => dropPdf(r.id, e.target.files && e.target.files[0])} />
          <button style={{ ...btn(false), padding: '5px 10px' }}
            onClick={() => fileRefs.current[r.id] && fileRefs.current[r.id].click()}>📄 Result</button>
        </>}
        {r.status === 'resulted' && r.attachment_id &&
          <button style={{ ...btn(true), padding: '5px 10px' }} title="Save the lab's result report to this computer"
            onClick={async () => { try { const x = await axios.get(`${API}/api/emr/attachments/${r.attachment_id}/download`, hdrs());
              await saveUrlAsFile(x.data.url, x.data.filename || `${r.specimen_id}-report.pdf`); } catch (e) { alert('Could not download the report'); } }}>⬇ Download report</button>}
      </td>
    </tr>
  );

  return (
    <div style={{ padding: 20, maxWidth: 1300, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 17 }}>Labs</strong>
        <select value={facilityId} onChange={e => setFacilityId(e.target.value)}
          style={{ font: 'inherit', fontSize: 13, padding: '7px 10px', borderRadius: 8, border: `1px solid ${T.b300}` }}>
          {facilities.map(x => <option key={x.id} value={x.id}>{x.org_name}</option>)}
        </select>
        <span style={{ flex: 1 }} />
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {statBox(summary.collectedToday, 'Collected Today', T.info)}
          {statBox(summary.pending, 'At Lab (Sent)', T.warning)}
          {statBox(summary.standing, 'Standing Orders', T.ink)}
          {statBox(summary.resulted, 'Resulted', T.pass)}
        </div>
      </div>

      {toast && <div style={{ ...card, padding: 10, marginBottom: 12, fontWeight: 700, fontSize: 13,
        background: toast.ok ? T.passBg : T.blockingBg, color: toast.ok ? T.pass : T.blocking }}>{toast.msg}</div>}

      {/* Pending provider review */}
      {!!pendingOrders.length && (
        <div style={{ ...card, padding: 14, marginBottom: 16, background: T.warningBg, border: `1px solid ${T.warning}` }}>
          <div style={{ fontWeight: 800, fontSize: 14, color: T.warning, marginBottom: 8 }}>
            ⚠ {pendingOrders.length} Standing Order{pendingOrders.length === 1 ? '' : 's'} Pending Provider Review</div>
          {pendingOrders.map(po => (
            <div key={po.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0',
              borderTop: `1px solid rgba(0,0,0,0.06)`, fontSize: 13 }}>
              <Avatar patientId={po.patient_id} photoKey={po.photo_key} first={po.first_name} last={po.last_name} size={30} />
              <span style={{ fontWeight: 800 }}>{po.last_name}, {po.first_name}</span>
              <span style={{ color: T.inkSoft, fontSize: 12 }}>{po.panel_name} · {po.test_count} tests · {fmtDT(po.created_at)}
                {po.ordered_via === 'standing_auto' && ' · auto-created on admission'}</span>
              <span style={{ flex: 1 }} />
              <button style={{ ...btn(true), padding: '6px 14px' }}
                onClick={async () => {
                  try {
                    await axios.post(`${API}/api/emr/labs/orders/${po.id}/review`, {}, hdrs());
                    flash('Order activated'); load();
                  } catch (e) { flash((e.response && e.response.data && e.response.data.error) || 'Only a prescriber can activate orders', false); }
                }}>Review & Activate</button>
            </div>
          ))}
        </div>
      )}

      {/* Quick actions */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <button style={btn(false)} onClick={() => setPicker('order')}>➕ New / One-Time Order</button>
        <button style={btn(false)} onClick={() => setPicker('upload')}>📄 Upload Result</button>
        <button style={btn(false)} onClick={openLabsDir}>🏥 Lab Directory</button>
        <button style={btn(false)} onClick={() => setShowResulted(s => !s)}>
          {showResulted ? 'Hide Resulted' : `Requisition History (${resultedReqs.length})`}</button>
      </div>

      {/* Standing orders */}
      <div style={{ ...card, padding: 16, marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 16 }}>Standing Lab Orders</div>
            <div style={{ fontSize: 12, color: T.inkSoft }}>Check who's testing today, then generate.</div>
          </div>
          <button style={btn(true)} disabled={!nChecked || busy} onClick={generate}>
            {busy ? 'Generating…' : `Generate Requisitions (${nChecked})`}</button>
        </div>
        {!!lastBatch.length && (
          <div style={{ background: T.infoBg, borderRadius: 8, padding: '8px 12px', marginBottom: 10,
            display: 'flex', gap: 10, alignItems: 'center', fontSize: 13, fontWeight: 600 }}>
            <span>{lastBatch.length} ready:</span>
            <button style={{ ...btn(true), padding: '6px 12px' }} onClick={() => printBatch(lastBatch, false)}>🖨 Print Requisitions</button>
            <button style={{ ...btn(false), padding: '6px 12px' }} onClick={() => printBatch(lastBatch, true)}>🏷 Print Labels</button>
            <button style={{ ...btn(false), padding: '6px 12px' }} onClick={() => openReqAt(lastBatch, 0)}>👁 Review</button>
            <span style={{ flex: 1 }} />
            <span style={{ cursor: 'pointer', color: T.inkSoft }} onClick={() => setLastBatch([])}>✕</span>
          </div>
        )}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr style={{ textAlign: 'left', color: T.inkSoft, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>
            <th style={{ padding: 6 }}><input type="checkbox" checked={!!orders.length && nChecked === orders.length} onChange={toggleAll} /></th>
            <th style={{ padding: 6 }}>Patient</th><th>Panel</th><th>Frequency</th><th>Last Collected</th><th>Last Status</th><th></th>
          </tr></thead>
          <tbody>
            {orders.map(o => {
              const d = daysSince(o.last_collected_at);
              return (
                <tr key={o.order_id} style={{ borderTop: `1px solid ${T.b100}`, background: checked[o.order_id] ? T.infoBg : 'transparent' }}>
                  <td style={{ padding: 6 }}><input type="checkbox" checked={!!checked[o.order_id]}
                    onChange={e => setChecked(c => ({ ...c, [o.order_id]: e.target.checked }))} /></td>
                  <td style={{ padding: 6 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <Avatar patientId={o.patient_id} photoKey={o.photo_key} first={o.first_name} last={o.last_name} />
                      <div><div style={{ fontWeight: 700 }}>{o.last_name}, {o.first_name}</div>
                        <div style={{ fontSize: 11, color: T.inkSoft }}>{o.mrn} · {o.loc}</div></div>
                    </div>
                  </td>
                  <td>{o.panel_name || '—'}</td>
                  <td>{o.frequency || 'As needed'}</td>
                  <td>{o.last_collected_at ? `${fmtDT(o.last_collected_at)}${d != null ? ` (${d}d ago)` : ''}` : <span style={{ color: T.warning, fontWeight: 700 }}>Never</span>}</td>
                  <td>{o.last_req_status ? (REQ_PILL[o.last_req_status] || (() => o.last_req_status))() : '—'}</td>
                  <td style={{ textAlign: 'right' }}>
                    <span style={{ cursor: 'pointer', color: T.rose, fontSize: 12, fontWeight: 700 }}
                      onClick={() => toggleTests(o)}>{openTests[o.order_id] ? 'Hide tests ▴' : 'Tests ▾'}</span>
                  </td>
                </tr>
              );
            })}
            {orders.map(o => openTests[o.order_id] ? (
              <tr key={o.order_id + '-tests'}>
                <td colSpan={7} style={{ background: '#faf7f8', padding: '8px 12px' }}>
                  <div style={{ fontSize: 11, color: T.inkSoft, marginBottom: 6 }}>
                    Uncheck any test to exclude it from the NEXT requisition for {o.first_name}. The standing order itself is unchanged.</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px' }}>
                    {openTests[o.order_id].map(t => { const k = t.test_code || t.test_name; return (
                      <label key={k} style={{ fontSize: 12, display: 'inline-flex', gap: 5, alignItems: 'center', cursor: 'pointer' }}>
                        <input type="checkbox"
                          checked={!(excluded[o.order_id] && excluded[o.order_id][k])}
                          onChange={e => setExcluded(x => ({ ...x,
                            [o.order_id]: { ...(x[o.order_id] || {}), [k]: !e.target.checked } }))} />
                        {t.test_name}
                      </label>
                    ); })}
                  </div>
                </td>
              </tr>
            ) : null)}
            {!orders.length && <tr><td colSpan={7} style={{ padding: 16, color: T.inkSoft, textAlign: 'center' }}>
              No active standing orders at this facility. Standing orders are placed by the prescriber from the patient chart's Labs section.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Requisitions */}
      <div style={{ ...card, padding: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ fontWeight: 800, fontSize: 16 }}>Lab Test Requisitions</div>
          {nReqChecked > 0 && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: T.inkSoft }}>{nReqChecked} selected:</span>
              <button style={{ ...btn(true), padding: '6px 12px' }} onClick={openChecked}>👁 Review All</button>
              <button style={{ ...btn(false), padding: '6px 12px' }} onClick={batchSend}>{labAcct ? 'Send All to Lab' : 'Mark All Sent'}</button>
              <button style={{ ...btn(false), padding: '6px 12px' }}
                onClick={() => printBatch(reqs.filter(r => reqChecked[r.id]).map(r => r.id), false)}>🖨</button>
              <button style={{ ...btn(false), padding: '6px 12px' }}
                onClick={() => printBatch(reqs.filter(r => reqChecked[r.id]).map(r => r.id), true)}>🏷</button>
            </div>
          )}
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr style={{ textAlign: 'left', color: T.inkSoft, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>
            <th style={{ padding: 6 }}></th>
            <th style={{ padding: 6 }}>Patient</th><th>Specimen</th><th>Source</th><th>Collected</th><th>Status</th><th style={{ textAlign: 'right' }}>Actions</th>
          </tr></thead>
          <tbody>
            {activeReqs.map(ReqRow)}
            {!activeReqs.length && <tr><td colSpan={7} style={{ padding: 16, color: T.inkSoft, textAlign: 'center' }}>No open requisitions.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* ---------- Detail modal with tabs + paging ---------- */}
      {viewReq && (() => {
        const r = viewReq.requisition, tests = viewReq.tests || [];
        const ins = r.insurance_snapshot || {};
        const meds = Array.isArray(r.meds_snapshot) ? r.meds_snapshot : [];
        const dx = Array.isArray(r.dx_snapshot) ? r.dx_snapshot : [];
        const md = ((r.md_first || '') + ' ' + (r.md_last || '')).trim();
        const F = ({ l, v }) => (
          <div style={{ minWidth: 150 }}>
            <div style={{ fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: T.inkSoft, fontWeight: 700 }}>{l}</div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{v || '—'}</div>
          </div>
        );
        const H = ({ children }) => <div style={{ fontWeight: 800, fontSize: 13, borderBottom: `1px solid ${T.b200}`, paddingBottom: 4, margin: '16px 0 8px' }}>{children}</div>;
        const hist = [
          { at: r.created_at, ev: 'Requisition created' },
          { at: r.collected_at, ev: `Specimen collected${r.collected_by_name ? ' by ' + r.collected_by_name : ''}` },
          { at: r.sent_at, ev: 'Sent to lab' },
          r.status === 'resulted' ? { at: null, ev: 'Result attached (see chart Labs)' } : null,
          r.voided_at ? { at: r.voided_at, ev: 'Voided: ' + (r.void_reason || '') } : null,
        ].filter(Boolean).filter(h => h.at || h.ev.startsWith('Result'));
        return (
          <div onClick={() => setViewReq(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div onClick={e => e.stopPropagation()} style={{ ...card, width: '100%', maxWidth: 820, maxHeight: '88vh', overflowY: 'auto', padding: 22 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div>
                  <div style={{ fontWeight: 900, fontSize: 18 }}>Lab Test Requisition</div>
                  <div style={{ fontWeight: 800, fontSize: 14 }}>{r.first_name} {r.last_name}</div>
                  <img src={barcodeDataUrl(r.specimen_id)} alt="" style={{ height: 40, marginTop: 6 }} />
                  <div style={{ fontFamily: 'monospace', fontSize: 12, letterSpacing: 1 }}>{r.specimen_id}</div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {(REQ_PILL[r.status] || (() => r.status))()}
                  {viewList.length > 1 && (
                    <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center', fontSize: 12, fontWeight: 800 }}>
                      <button style={{ ...btn(false), padding: '5px 10px' }} disabled={viewIdx === 0} onClick={() => pageView(-1)}>‹ Prev</button>
                      {viewIdx + 1} of {viewList.length}
                      <button style={{ ...btn(false), padding: '5px 10px' }} disabled={viewIdx === viewList.length - 1} onClick={() => pageView(1)}>Next ›</button>
                    </span>
                  )}
                  <button style={{ ...btn(false), padding: '6px 12px' }} onClick={() => printBatch([r.id], false)}>🖨</button>
                  <button style={{ ...btn(false), padding: '6px 12px' }} onClick={() => printBatch([r.id], true)}>🏷</button>
                  {r.status === 'collected' && <button style={{ ...btn(true), padding: '6px 12px' }}
                    onClick={async () => { await send(r.id); openReqAt(viewList, viewIdx); }}>{labAcct ? 'Send to Lab' : 'Mark Sent'}</button>}
                  <span onClick={() => setViewReq(null)} style={{ cursor: 'pointer', color: T.inkSoft, fontWeight: 800, fontSize: 16, padding: '0 4px' }}>✕</span>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 6, margin: '14px 0 4px' }}>
                {[['req', 'Requisition'], ['tests', `Tests (${tests.length})`], ['history', 'History']].map(([k, l]) => (
                  <span key={k} onClick={() => setViewTab(k)} style={{
                    padding: '7px 14px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                    background: viewTab === k ? T.rose : '#fff', color: viewTab === k ? '#fff' : T.ink,
                    border: `1px solid ${viewTab === k ? T.rose : T.b300}` }}>{l}</span>
                ))}
              </div>

              {viewTab === 'req' && <div>
                <H>Specimen</H>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 24px' }}>
                  <F l="Source" v={r.specimen_source} /><F l="Collected" v={fmtDT(r.collected_at)} />
                  <F l="Collected By" v={r.collected_by_name} /><F l="Sent" v={fmtDT(r.sent_at)} />
                </div>
                <H>Patient</H>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 24px' }}>
                  <F l="Name" v={`${r.first_name} ${r.last_name}`} /><F l="MRN" v={r.mrn} />
                  <F l="DOB" v={r.dob ? new Date(r.dob).toLocaleDateString('en-US', { timeZone: 'UTC' }) : null} />
                </div>
                <H>Insurance (snapshot at collection)</H>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 24px' }}>
                  <F l="Payer" v={ins.primary_payer} /><F l="Member ID" v={ins.member_id} />
                  <F l="Group" v={ins.group_number} /><F l="Snapshot" v={ins.snapshot_at ? fmtDT(ins.snapshot_at) : null} />
                </div>
                <H>Lab & Provider</H>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 24px' }}>
                  <F l="Lab" v={r.lab_name} /><F l="Account" v={r.account_number} /><F l="CLIA" v={r.clia_code} />
                  <F l="Ordering Provider" v={md ? md + (r.md_credentials ? ', ' + r.md_credentials : '') : null} />
                </div>
                <H>Statement of Medical Necessity</H>
                <div style={{ fontSize: 12.5, fontFamily: 'monospace', whiteSpace: 'pre-wrap', background: '#faf7f8', borderRadius: 8, padding: 10 }}>
                  {r.medical_necessity || '—'}</div>
                <H>Clinical Information</H>
                {r.status === 'collected' ? (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input maxLength={60} defaultValue={r.clinical_info || ''} id={'ci-' + r.id}
                      placeholder="Up to 60 characters — prints on the requisition"
                      style={{ font: 'inherit', fontSize: 13, padding: '7px 10px', borderRadius: 8, border: `1px solid ${T.b300}`, flex: 1 }} />
                    <button style={{ ...btn(true), padding: '7px 14px' }}
                      onClick={() => saveClinicalInfo(r.id, document.getElementById('ci-' + r.id).value)}>Save</button>
                  </div>
                ) : <div style={{ fontSize: 12.5 }}>{r.clinical_info || '—'}</div>}
                <H>Diagnoses</H>
                <div style={{ fontSize: 12.5 }}>{dx.length ? dx.map(d => d.code + ' ' + (d.description || '')).join(', ') : '—'}</div>
                <H>Medications</H>
                <div style={{ fontSize: 12.5 }}>{meds.length ? meds.join(', ') : '—'}</div>
              </div>}

              {viewTab === 'tests' && (() => {
                const editable = r.status === 'collected';
                const list = orderTests.length ? orderTests : tests;
                const nInc = list.filter(t => !stagedEx[t.test_code || t.test_name]).length;
                const primaryDx = ((dx.find(d => d.is_primary) || dx[0]) || {}).code || '';
                const setAll = (exVal) => {
                  const ex = {};
                  if (exVal) for (const t of list) ex[t.test_code || t.test_name] = true;
                  setStagedEx(ex); setTestsDirty(true);
                };
                const kindPill = (k) => pill(k === 'Screen' ? T.infoBg : k === 'Validity' ? T.b100 : T.warningBg,
                  k === 'Screen' ? T.info : k === 'Validity' ? T.inkSoft : T.warning, k);
                return (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '10px 0 6px' }}>
                      <div style={{ fontSize: 12, color: T.inkSoft }}>
                        {editable ? 'Deselect any tests that should not be included in this requisition.' : 'Tests are locked — specimen already sent.'}</div>
                      <div style={{ fontSize: 12, fontWeight: 800, display: 'flex', gap: 12, alignItems: 'center' }}>
                        {nInc} Tests Selected
                        {editable && <>
                          <span style={{ color: T.rose, cursor: 'pointer' }} onClick={() => setAll(true)}>Select None</span>
                          <span style={{ color: T.rose, cursor: 'pointer' }} onClick={() => setAll(false)}>Select All</span>
                        </>}
                      </div>
                    </div>
                    <div style={{ border: `1px solid ${T.b200}`, borderRadius: 10, overflow: 'hidden' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '30px 1fr 150px 80px 70px 60px', padding: '8px 10px',
                        background: '#faf7f8', fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: T.inkSoft, fontWeight: 800 }}>
                        <div /><div>Test</div><div>Class</div><div>Type</div><div>Dx</div><div style={{ textAlign: 'right' }}>Included</div>
                      </div>
                      <div style={{ maxHeight: 340, overflowY: 'auto' }}>
                        {list.map((t, i) => {
                          const k = t.test_code || t.test_name;
                          const inc = !stagedEx[k];
                          return (
                            <div key={k} style={{ display: 'grid', gridTemplateColumns: '30px 1fr 150px 80px 70px 60px',
                              padding: '7px 10px', alignItems: 'center', borderTop: `1px solid ${T.b100}`,
                              background: inc ? '#fff' : '#fdf6f7', opacity: inc ? 1 : 0.65, fontSize: 12.5 }}>
                              <div><input type="checkbox" disabled={!editable} checked={inc}
                                onChange={e => { setStagedEx(x => ({ ...x, [k]: !e.target.checked })); setTestsDirty(true); }} /></div>
                              <div style={{ fontWeight: 700 }}>{t.test_name}
                                {t.test_code && <span style={{ fontSize: 10, color: T.inkSoft, fontFamily: 'monospace', marginLeft: 5 }}>({t.test_code})</span>}</div>
                              <div style={{ color: T.inkSoft }}>{testClass(t.test_name)}</div>
                              <div>{kindPill(testKind(t.test_name))}</div>
                              <div style={{ fontFamily: 'monospace', fontSize: 11 }}>{primaryDx}</div>
                              <div style={{ textAlign: 'right' }}>{inc ? <span style={{ color: T.pass, fontWeight: 800 }}>✓</span> : <span style={{ color: T.inkSoft }}>—</span>}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    {editable && (
                      <div>
                        <div style={{ background: T.passBg, color: T.pass, borderRadius: 8, padding: '8px 12px', fontSize: 12, fontWeight: 700, margin: '10px 0' }}>
                          ✓ Only selected tests will be included in this requisition. The standing order is unchanged.</div>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                          <button style={btn(false)} onClick={() => openReqAt(viewList, viewIdx, true)}>Cancel</button>
                          <button style={btn(false)} disabled={!testsDirty} onClick={() => saveTests(false)}>Save Changes</button>
                          <button style={btn(true)} disabled={!testsDirty} onClick={() => saveTests(true)}>Apply & Close</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {viewTab === 'history' && <div style={{ marginTop: 10 }}>
                {hist.map((h, i) => (
                  <div key={i} style={{ display: 'flex', gap: 12, padding: '8px 0', borderBottom: `1px solid ${T.b100}`, fontSize: 13 }}>
                    <span style={{ color: T.inkSoft, minWidth: 140 }}>{h.at ? fmtDT(h.at) : '—'}</span>
                    <span style={{ fontWeight: 600 }}>{h.ev}</span>
                  </div>
                ))}
              </div>}
            </div>
          </div>
        );
      })()}

      {tempFor && <BoardTempModal API={API} hdrs={hdrs} T={T} btn={btn} card={card} req={tempFor} flash={flash} onClose={() => setTempFor(null)} onSaved={tempSaved} />}
      {(() => { const stale = reqs.filter(r => r.status === 'collected' && r.collected_at && Date.now() - new Date(r.collected_at).getTime() > 2 * 3600000); return stale.length ? (
        <div style={{ position: 'fixed', bottom: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 1050, maxWidth: 560, background: '#FEF3F2', border: '1px solid #F04438', color: '#B42318', borderRadius: 10, padding: '10px 14px', fontSize: 13, fontWeight: 600, boxShadow: '0 2px 8px rgba(20,45,70,0.12)' }}>
          {stale.length} specimen{stale.length === 1 ? '' : 's'} collected over 2 hours ago and not yet sent to the lab. Record the temperature and press Send to Lab on each row.
        </div>) : null; })()}
      {/* ---------- Quick-action pickers ---------- */}
      {picker && (
        <div onClick={() => setPicker(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 1100,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ ...card, width: '100%', maxWidth: 460, maxHeight: '70vh', overflowY: 'auto', padding: 18 }}>
            {picker === 'order' && <BoardOrderFlow API={API} hdrs={hdrs} T={T} btn={btn} facilityId={facilityId} flash={flash} onChanged={load} onClose={() => setPicker(null)} />}
            {picker === 'upload' && <>
              <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 4 }}>Upload Result</div>
              <div style={{ fontSize: 12, color: T.inkSoft, marginBottom: 10 }}>Pick the requisition this result belongs to.</div>
              {reqs.filter(r => r.status === 'sent' || r.status === 'collected').map(r => (
                <div key={r.id} onClick={() => { fileRefs.current[r.id] && fileRefs.current[r.id].click(); }}
                  style={{ padding: '8px 10px', borderRadius: 8, cursor: 'pointer', fontSize: 13, borderBottom: `1px solid ${T.b100}`,
                    display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: 700 }}>{r.last_name}, {r.first_name}</span>
                  <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{r.specimen_id}</span>
                </div>
              ))}
              {!reqs.filter(r => r.status === 'sent' || r.status === 'collected').length &&
                <div style={{ color: T.inkSoft, fontSize: 13 }}>No open requisitions awaiting results.</div>}
            </>}
            {picker === 'labs' && <>
              <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 10 }}>Lab Directory</div>
              {labsDir.map(l => (
                <div key={l.id} style={{ padding: '8px 0', borderBottom: `1px solid ${T.b100}`, fontSize: 13 }}>
                  <div style={{ fontWeight: 700 }}>{l.name}</div>
                  <div style={{ fontSize: 11, color: T.inkSoft }}>Account {l.account_number || '—'} · CLIA {l.clia_code || '—'}{l.phone ? ' · ' + l.phone : ''}</div>
                </div>
              ))}
              {!labsDir.length && <div style={{ color: T.inkSoft, fontSize: 13 }}>No labs configured.</div>}
            </>}
          </div>
        </div>
      )}
    </div>
  );
}
